import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IDssContext, IDssProps } from "./dss.interface";
import { DimensionMapper, IDSSForm, IFormValues, ScoreKey } from "../../interfaces/dss.interface";
import { useGetDSSForm, useGetDssFormValues } from "../../hooks/useDSS";
import { TranslationContext } from "../translation/translation.provider";

const dssInitValues: IDssContext = {
    forms: [],
    setForms: Function.prototype(),
    resetForm: Function.prototype(),
    updateForms: Function.prototype()
};

export const DssContext = createContext<IDssContext>(dssInitValues);

const DssProvider = ({ children }: IDssProps) => {
    const { lang } = useContext(TranslationContext);
    const getForms = useGetDSSForm();
    const getValues = useGetDssFormValues();
    const [forms, setForms] = useState<IDSSForm[]>([]);

    const parseForms = useCallback(
        (forms: IDSSForm[], values: IFormValues) =>
            forms.map((f) => {
                const dimensionKey = DimensionMapper[f.dimension];

                const value = values[dimensionKey]?.score;
                const date = values[dimensionKey]?.date
                    ? new Date(values[dimensionKey].date)
                    : undefined;
                    
                return {
                    ...f,
                    value: isNaN(value) || value === null ? undefined : Math.round(value),
                    last_updated: date
                };
            }),
        []
    );

    const updateForms = useCallback(async () => {
        const valuesResponse = await getValues.mutateAsync();
        if (!valuesResponse.success) return false;

        const values = valuesResponse.data as IFormValues;

        setForms((forms) => parseForms(forms, values));

        return true;
    }, [getValues, setForms]);

    const handleFormIds = useCallback(async (lang: string) => {
        const [valuesResponse, response] = await Promise.all([
            getValues.mutateAsync(),
            getForms.mutateAsync({ locale: lang })
        ]);

        if (!response.success || !valuesResponse.success) return;

        const values = valuesResponse.data as IFormValues;
        let forms = response.data as IDSSForm[];
        forms = parseForms(forms, values);
        
        setForms(forms);
    }, []);

    const resetForm = useCallback(
        async (form: IDSSForm) => {
            setForms((forms) =>
                forms.map((f) => {
                    if (f.formId === form.formId) return { ...f, value: undefined };
                    else return f;
                })
            );
        },
        [setForms]
    );

    useEffect(() => {
        if (!lang) return;
        handleFormIds(lang);
    }, [handleFormIds, lang]);

    return (
        <DssContext.Provider value={{ forms, setForms, resetForm, updateForms }}>
            {children}
        </DssContext.Provider>
    );
};

export default DssProvider;
